import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFightData } from '../contexts/FightDataContext';
import { Question, Eyeglasses, Sun, Moon, ChartLine } from 'phosphor-react';
import ClickableElement from './ClickableElement';
import { analytics } from '../utils/analytics';

// CSS for the logo hover effect
const logoGradientStyle = `
  @keyframes quickPulse {
    0% {
      color: inherit;
    }
    50% {
      color: #3b82f6;
    }
    100% {
      color: inherit;
    }
  }
  
  .logo-container .logo-text,
  .logo-container .logo-icon {
    /* Ensure animation doesn't cause layout shifts */
    will-change: color;
  }
  
  .logo-container:hover .logo-text,
  .logo-container:hover .logo-icon {
    animation: quickPulse 0.6s ease-out forwards;
  }
`;

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode, toggleColorMode } = useTheme();
  const { refreshStatus } = useFightData();

  const goToHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const goToMethodology = () => {
    navigate('/methodology');
  };

  const goToTracker = () => {
    navigate('/prediction-tracker');
  };

  const handleThemeToggle = () => {
    toggleColorMode();
    analytics.trackEvent('theme_toggle', {
      new_theme: colorMode === 'light' ? 'dark' : 'light'
    });
  };

  return (
    <>
      <style>{logoGradientStyle}</style>
      <header className={`fixed top-0 left-0 right-0 z-10 bg-white-only dark:bg-dark-background border-b border-border dark:border-dark-border ${className}`}>
        <div className="mx-auto w-full max-w-[1800px] 2xl:border-x border-border dark:border-dark-border">
          <div className="flex items-center justify-between py-4 px-4 md:px-6">
            <div className="flex items-center space-x-2">
              <ClickableElement
                as="button"
                onClick={goToHome}
                className="flex items-center space-x-2 logo-container"
              >
                <Eyeglasses className="h-7 w-7 text-text-primary dark:text-dark-text-primary logo-icon" weight="bold" />
                <h1 className="text-2xl font-bold text-text-primary dark:text-dark-text-primary tracking-tight logo-text">
                  fightvision
                </h1>
              </ClickableElement>
              {refreshStatus && (
                <span className="text-xs text-primary dark:text-dark-primary font-medium ml-2 px-2 py-1 bg-background-secondary dark:bg-dark-background-secondary rounded-full">
                  {refreshStatus}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Prediction Tracker Button with Tooltip */}
              <div className="relative group">
                <ClickableElement
                  as="button" 
                  onClick={goToTracker}
                  className="p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95"
                >
                  <ChartLine className="h-6 w-6" weight="regular" />
                </ClickableElement>
                <div className="absolute left-1/2 transform -translate-x-1/2 top-[56px] opacity-0 group-hover:opacity-100 transition-all duration-200 animate-in fade-in pointer-events-none z-10">
                  <div className="relative bg-zinc-800 text-white text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-md invisible md:visible">
                    Track predictions
                    <div className="absolute left-1/2 transform -translate-x-1/2 -top-1.5 w-3 h-3 bg-zinc-800 rotate-45"></div>
                  </div>
                </div>
              </div>
              
              {/* Question Mark Button with Tooltip */}
              <div className="relative group">
                <ClickableElement
                  as="button" 
                  onClick={goToMethodology}
                  className="p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95"
                >
                  <Question className="h-6 w-6" weight="regular" />
                </ClickableElement>
                <div className="absolute left-1/2 transform -translate-x-1/2 top-[56px] opacity-0 group-hover:opacity-100 transition-all duration-200 animate-in fade-in pointer-events-none z-10">
                  <div className="relative bg-zinc-800 text-white text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-md invisible md:visible">
                    Methodology
                    <div className="absolute left-1/2 transform -translate-x-1/2 -top-1.5 w-3 h-3 bg-zinc-800 rotate-45"></div>
                  </div>
                </div>
              </div>
              
              {/* Dark Mode Toggle Button with Tooltip */}
              <div className="relative group">
                <ClickableElement
                  as="button" 
                  onClick={handleThemeToggle}
                  className="p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95"
                >
                  {colorMode === 'light' ? (
                    <Moon className="h-6 w-6" weight="regular" />
                  ) : (
                    <Sun className="h-6 w-6" weight="regular" />
                  )}
                </ClickableElement>
                <div className="absolute right-0 top-[56px] opacity-0 group-hover:opacity-100 transition-all duration-200 animate-in fade-in pointer-events-none z-10">
                  <div className="relative bg-zinc-800 text-white text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-md invisible md:visible">
                    {colorMode === 'light' ? 'Dark mode' : 'Light mode'}
                    <div className="absolute right-3 -top-1.5 w-3 h-3 bg-zinc-800 rotate-45"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header; 